import React, { useMemo } from 'react';
import { XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
  priority: 'critical' | 'required';
}

interface PasswordStrengthIndicatorProps {
  password: string;
  email?: string;
  onValidationChange?: (isValid: boolean, failedRequirements: string[]) => void;
  showRequirements?: boolean;
}

export function PasswordStrengthIndicator({
  password,
  email,
  onValidationChange,
  showRequirements = true,
}: PasswordStrengthIndicatorProps) {
  // Context-specific terms to block
  const contextTerms = useMemo(() => ['kestrel', 'kubernetes', 'k8s', 'cluster', 'security'], []);

  const requirements: PasswordRequirement[] = useMemo(() => [
    {
      id: 'length-min',
      label: 'At least 12 characters',
      validator: (pwd: string) => pwd.length >= 12,
      priority: 'required'
    },
    {
      id: 'length-max',
      label: 'No more than 32 characters',
      validator: (pwd: string) => pwd.length <= 32,
      priority: 'required'
    },
    {
      id: 'no-context',
      label: 'No service-related terms',
      validator: (pwd: string) => {
        const lowerPwd = pwd.toLowerCase();
        return !contextTerms.some(term => lowerPwd.includes(term));
      },
      priority: 'critical'
    },
    {
      id: 'no-sequential',
      label: 'No sequential characters (abc, 123)',
      validator: (pwd: string) => {
        // Check for sequential letters or numbers
        for (let i = 0; i < pwd.length - 2; i++) {
          const char1 = pwd.charCodeAt(i);
          const char2 = pwd.charCodeAt(i + 1);
          const char3 = pwd.charCodeAt(i + 2);
          
          if (char2 === char1 + 1 && char3 === char2 + 1) {
            return false;
          }
        }
        return true;
      },
      priority: 'critical'
    },
    {
      id: 'no-repeated',
      label: 'No excessive character repetition',
      validator: (pwd: string) => {
        // Check for same character repeated more than 2 times
        return !/(.)\1{2,}/.test(pwd);
      },
      priority: 'critical'
    }
  ], [contextTerms]);

  // Calculate password strength
  const strength = useMemo(() => {
    if (!password) return { score: 0, label: 'Empty', color: 'bg-muted', textColor: 'text-muted-foreground' };
    
    const results = requirements.map(req => ({
      ...req,
      passed: req.validator(password)
    }));
    
    const totalRequirements = requirements.length;
    const passedRequirements = results.filter(r => r.passed).length;
    const score = Math.round((passedRequirements / totalRequirements) * 100);
    
    // Critical requirements failed = weak
    const criticalFailed = results.filter(r => r.priority === 'critical' && !r.passed);
    if (criticalFailed.length > 0) {
      return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600 dark:text-red-400' };
    }
    
    // Some required requirements failed = medium
    const requiredFailed = results.filter(r => r.priority === 'required' && !r.passed);
    if (requiredFailed.length > 0) {
      return { score, label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400' };
    }
    
    // All requirements passed = strong
    return { score: 100, label: 'Strong', color: 'bg-green-600', textColor: 'text-green-600 dark:text-green-400' };
  }, [password, requirements]);

  // Track validation state
  React.useEffect(() => {
    if (!onValidationChange) return;
    
    const results = requirements.map(req => ({
      ...req,
      passed: req.validator(password)
    }));
    
    const failedCritical = results.filter(r => r.priority === 'critical' && !r.passed);
    const failedRequired = results.filter(r => r.priority === 'required' && !r.passed);
    
    const isValid = failedCritical.length === 0 && failedRequired.length === 0;
    
    const failedRequirements = [
      ...failedCritical.map(r => r.label),
      ...failedRequired.map(r => r.label)
    ];
    
    onValidationChange(isValid, failedRequirements);
  }, [password, requirements, onValidationChange]);

  const failedResults = useMemo(
    () => requirements.filter(req => !req.validator(password)),
    [password, requirements]
  );

  return (
    <div className="space-y-1.5">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="h-1 flex-1 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full transition-all duration-300", strength.color)}
            style={{ width: `${strength.score}%` }}
          />
        </div>
        <span className={cn("text-xs font-medium shrink-0", strength.textColor)}>
          {strength.label}
        </span>
      </div>

      {/* Only unmet requirements; each disappears once satisfied, so the
          block shrinks as the user types and vanishes when the password is
          valid — keeps the card compact. */}
      {showRequirements && password && failedResults.length > 0 && (
        <ul className="space-y-0.5">
          {failedResults.map(req => (
            <li key={req.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <XCircle className="h-3 w-3 flex-shrink-0" />
              {req.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
